import React, { useEffect, useState } from 'react';
import {
  Copy,
  Check,
  Users,
  UserCheck,
  Clock,
  UserX,
  DollarSign,
  TrendingUp,
  Bell,
} from 'lucide-react';
import {
  StatCard
} from '@/components/ui/StatCard';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { ProgressStep } from '@/components/ui/ProgressStep';
import { Panigation } from '@/components/ui/Panigation';
import { SearchFilterBar } from '@/components/ui/SearchFilterBar';
import { fetchAffiliateBonusTracker, fetchCommissionHistory } from './function';


type UserInfo = {
  id: number;
  fullname: string | null;
  email: string;
  avatar: string | null;
  phone: string | null;
  gender: string | null;
  birthday: string | null;
  job_title: string | null;
};

type ReferralTrackerItem = {
  signup: boolean;
  kyc: boolean;
  deposit: boolean;
  daysLeft: string;
  status: 'credited' | 'pending' | 'expired';
  progress: number;
  user_info: UserInfo;
};

type AffiliateHistoryItem = {
  date: string;
  user_info: UserInfo;
  earned: string;
  pending: string;
};

const Referrals: React.FC = () => {
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);

  // Tracker data state
  const [referralTrackerDataState, setReferralTrackerDataState] = useState<ReferralTrackerItem[]>([]);
  const trackerLimit = 10;
  const [trackerTotal, setTrackerTotal] = useState(0);
  const [trackerPage, setTrackerPage] = useState(1);

  // useEffect(() => {
  //   // Fetch tracker data whenever trackerPage changes
  //   fetchAffiliateBonusTracker(trackerPage, trackerLimit).then(({ data, total }) => {
  //     setReferralTrackerDataState(data.map(item => ({
  //       user_info: item.user_info,
  //       signup: item.progress.signup,
  //       kyc: item.progress.kyc,
  //       deposit: item.progress.deposit,
  //       daysLeft: item.days_left,
  //       status: item.bonus_status.toLowerCase() as 'credited' | 'pending' | 'expired',
  //       progress: item.action === 'completed' ? 100 : (item.progress.signup ? (item.progress.kyc ? (item.progress.deposit ? 100 : 66) : 33) : 0),
  //     })) || []);
  //     setTrackerTotal(total);
  //   });
  // }, [trackerPage]);

  // commission-history
  const [affiliateHistory, setAffiliateHistory] = useState<AffiliateHistoryItem[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const historyLimit = 10;

  // useEffect(() => {
  //   // Fetch affiliate history data whenever historyPage changes'
  //   fetchCommissionHistory(historyLimit, historyPage).then(({ data, total }) => {
  //     setAffiliateHistory(data.map(item => ({
  //       date: item.date,
  //       user_info: item.user_info,
  //       earned: item.commission_earned,
  //       pending: item.pending_commission,
  //     })) || []);
  //     setHistoryTotal(total);
  //   });
  // }, [historyPage]);

  const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') || '{}') : null;
  const referralLink = user ? `${window.location.origin}/sign-up?ref=${user.referral_code}` : 'Non-existent';
  const referralId = user ? user.referral_code : "Non-existent";

  const handleCopy = (text: string, type: 'link' | 'id') => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text);
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }
      document.body.removeChild(textArea);
    }

    if (type === 'link') {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } else {
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="!bg-gray-100 dark:!bg-dark-card rounded-xl p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="!text-lg sm:!text-xl !font-bold !text-gray-900 dark:!text-white">{t('payment.referral.title')}</h2>
            <p className="!text-xs sm:!text-sm !text-gray-500 dark:!text-dark-text-secondary">{t('payment.referral.description')}</p>
          </div>
          <div className="flex items-center gap-3 bg-transparent dark:!bg-gradient-to-r dark:!from-green-900/50 dark:!to-green-800/30 !border !border-green-700/50 rounded-lg px-4 py-2.5">
            <span className="!text-xs sm:!text-sm text-primary dark:!text-green-400">{t('payment.referral.earning')}:</span>
            <span className="!text-lg sm:!text-xl !font-bold text-black dark:!text-green-400">0.00 đ</span>
            <span className="!text-xs !text-dark-text-secondary">VND</span>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex-1">
            <label className="!text-xs !text-gray-500 dark:!text-dark-text-secondary mb-1.5 block">{t('payment.referral.link')}</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                readOnly
                value={referralLink}
                className="flex-1 !px-3 sm:!px-4 !py-2 sm:!py-2.5 !bg-white dark:!bg-dark-bg !border !border-gray-200 dark:!border-dark-border !rounded-lg !text-gray-700 dark:!text-dark-text-secondary !text-xs sm:!text-sm !outline-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => handleCopy(referralLink, 'link')}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 !px-3 sm:!px-4 !py-2 sm:!py-2.5 !bg-gray-200 dark:!bg-dark-border !rounded-lg !text-gray-700 dark:!text-white !text-xs sm:!text-sm hover:!bg-gray-300 dark:hover:!bg-dark-border-light !transition-colors !border-none"
                >
                  {copiedLink ? <Check className="w-3.5 sm:w-4 h-3.5 sm:h-4" /> : <Copy className="w-3.5 sm:w-4 h-3.5 sm:h-4" />}
                  {t('payment.referral.copy')}
                </button>
                {/* <button className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 !px-3 sm:!px-4 !py-2 sm:!py-2.5 !bg-primary !rounded-lg !text-white !text-xs sm:!text-sm hover:!bg-primary-dark !transition-colors !border-none">
                  <Share2 className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
                  Share
                </button> */}
              </div>
            </div>
          </div>
          <div className="w-full lg:w-80">
            <label className="!text-xs !text-gray-500 dark:!text-dark-text-secondary mb-1.5 block">{t('payment.referral.id')}</label>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={referralId}
                className="flex-1 !px-3 sm:!px-4 !py-2 sm:!py-2.5 !bg-white dark:!bg-dark-bg !border !border-gray-200 dark:!border-dark-border !rounded-lg !text-gray-700 dark:!text-dark-text-secondary !text-xs sm:!text-sm !outline-none"
              />
              <button
                onClick={() => handleCopy(referralId, 'id')}
                className="flex items-center gap-1.5 sm:gap-2 !px-3 sm:!px-4 !py-2 sm:!py-2.5 !bg-gray-200 dark:!bg-dark-border !rounded-lg !text-gray-700 dark:!text-white !text-xs sm:!text-sm hover:!bg-gray-300 dark:hover:!bg-dark-border-light !transition-colors !border-none"
              >
                {copiedId ? <Check className="w-3.5 sm:w-4 h-3.5 sm:h-4" /> : <Copy className="w-3.5 sm:w-4 h-3.5 sm:h-4" />}
                {t('payment.referral.copy')}
              </button>
            </div>
          </div>
        </div>

        {/** Disable KYC */}
        {/* <div className="flex items-center gap-2 mt-4">
          <span className="!text-xs sm:!text-sm !text-gray-500 dark:!text-dark-text-secondary">KYC Status:</span>
          <StatusBadge status="notVerified" />
        </div> */}
      </div>


    </div>
  );
};

export default Referrals;




const Temp = () => {


  // Tracker data state
  const [referralTrackerDataState, setReferralTrackerDataState] = useState<ReferralTrackerItem[]>([]);
  const trackerLimit = 10;
  const [trackerTotal, setTrackerTotal] = useState(0);
  const [trackerPage, setTrackerPage] = useState(1);
  // commission-history
  const [affiliateHistory, setAffiliateHistory] = useState<AffiliateHistoryItem[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const historyLimit = 10;

  useEffect(() => {
    // Fetch affiliate history data whenever historyPage changes'
    fetchCommissionHistory(historyLimit, historyPage).then(({ data, total }) => {
      setAffiliateHistory(data.map(item => ({
        date: item.date,
        user_info: item.user_info,
        earned: item.commission_earned,
        pending: item.pending_commission,
      })) || []);
      setHistoryTotal(total);
    });
  }, [historyPage]);
  useEffect(() => {
    // Fetch tracker data whenever trackerPage changes
    fetchAffiliateBonusTracker(trackerPage, trackerLimit).then(({ data, total }) => {
      setReferralTrackerDataState(data.map(item => ({
        user_info: item.user_info,
        signup: item.progress.signup,
        kyc: item.progress.kyc,
        deposit: item.progress.deposit,
        daysLeft: item.days_left,
        status: item.bonus_status.toLowerCase() as 'credited' | 'pending' | 'expired',
        progress: item.action === 'completed' ? 100 : (item.progress.signup ? (item.progress.kyc ? (item.progress.deposit ? 100 : 66) : 33) : 0),
      })) || []);
      setTrackerTotal(total);
    });
  }, [trackerPage]);

  return <>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      <StatCard
        icon={<Users className="w-4 sm:w-5 h-4 sm:h-5 !text-primary" />}
        label="Total Referrals"
        value={0}
        iconBgColor="!bg-primary/20"
        valueColor="!text-gray-900 dark:!text-white"
      />
      <StatCard
        icon={<UserCheck className="w-4 sm:w-5 h-4 sm:h-5 !text-status-success" />}
        label="Active Referrals"
        value={0}
        iconBgColor="!bg-status-success/20"
        valueColor="!text-status-success"
      />
      <StatCard
        icon={<Clock className="w-4 sm:w-5 h-4 sm:h-5 !text-status-warning" />}
        label="Pending Bonuses"
        value={0}
        iconBgColor="!bg-status-warning/20"
        valueColor="!text-status-warning"
      />
      <StatCard
        icon={<UserX className="w-4 sm:w-5 h-4 sm:h-5 !text-status-error" />}
        label="Expired Referrals"
        value={0}
        iconBgColor="!bg-status-error/20"
        valueColor="!text-status-error"
      />
    </div>

    <div className="!bg-white dark:!bg-dark-card rounded-xl">
      <div className="px-4 sm:px-6 py-3 sm:py-4 !border-b !border-gray-200 dark:!border-dark-border flex items-center gap-3">
        <div className="p-2 !bg-primary/20 rounded-lg">
          <DollarSign className="w-4 h-4 !text-primary" />
        </div>
        <h2 className="!text-base sm:!text-lg !font-semibold !text-gray-900 dark:!text-white">Referral Bonus Tracker</h2>
      </div>
      <div className="p-4 sm:p-6">
        <SearchFilterBar />
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="min-w-[700px] px-4 sm:px-0">
            <table className="w-full">
              <thead>
                <tr className="!border-b !border-gray-200 dark:!border-dark-border">
                  <th className="px-3 sm:px-4 py-3 text-left !text-xs !font-medium !text-gray-500 dark:!text-dark-text-muted">Referral User</th>
                  <th className="px-3 sm:px-4 py-3 text-left !text-xs !font-medium !text-gray-500 dark:!text-dark-text-muted">Progress</th>
                  <th className="px-3 sm:px-4 py-3 text-left !text-xs !font-medium !text-gray-500 dark:!text-dark-text-muted">Days Left</th>
                  <th className="px-3 sm:px-4 py-3 text-left !text-xs !font-medium !text-gray-500 dark:!text-dark-text-muted">Bonus Status</th>
                  <th className="px-3 sm:px-4 py-3 text-left !text-xs !font-medium !text-gray-500 dark:!text-dark-text-muted">Actions</th>
                </tr>
              </thead>
              <tbody className="!divide-y !divide-gray-200 dark:!divide-dark-border">
                {referralTrackerDataState.map((item, index) => (
                  <tr key={index} className="hover:!bg-gray-50 dark:hover:!bg-dark-card-elevated/50">
                    <td className="px-3 sm:px-4 py-3 sm:py-4 !text-xs sm:!text-sm !text-gray-900 dark:!text-white">{item?.user_info?.fullname || ''}</td>
                    <td className="px-3 sm:px-4 py-3 sm:py-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
                          <ProgressStep label="Signup" completed={item.signup} />
                          <ProgressStep label="KYC" completed={item.kyc} />
                          <ProgressStep label="Deposit ≥ 100 USDT" completed={item.deposit} />
                        </div>
                        <div className="w-full h-1 sm:h-1.5 !bg-gray-200 dark:!bg-dark-border rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${item.status === 'expired' ? '!bg-status-error' : '!bg-status-success'}`}
                            style={{ width: `${item.progress}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 py-3 sm:py-4">
                      <span className={`!text-xs sm:!text-sm ${item.daysLeft === 'Completed' ? '!text-status-success' :
                        item.daysLeft === 'Expired' ? '!text-status-error' : '!text-status-warning'
                        }`}>
                        {item.daysLeft}
                      </span>
                    </td>
                    <td className="px-3 sm:px-4 py-3 sm:py-4">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="px-3 sm:px-4 py-3 sm:py-4">
                      <button className="flex items-center gap-1.5 !text-xs sm:!text-sm !text-gray-500 dark:!text-dark-text-secondary hover:!text-gray-700 dark:hover:!text-white !bg-transparent !border-none">
                        <Bell className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
                        Notify
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <Panigation currentPage={trackerPage} totalPages={Math.ceil(trackerTotal / trackerLimit)} onPageChange={setTrackerPage} />
      </div>
    </div>

    {/* Affiliate Stats Cards */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      <StatCard
        icon={<TrendingUp className="w-4 sm:w-5 h-4 sm:h-5 !text-status-success" />}
        label="Total Earnings"
        value="$0.00"
        iconBgColor="!bg-status-success/20"
        valueColor="!text-status-success"
      />
      <StatCard
        icon={<DollarSign className="w-4 sm:w-5 h-4 sm:h-5 !text-status-warning" />}
        label="This Month"
        value="$0.00"
        iconBgColor="!bg-status-warning/20"
        valueColor="!text-status-warning"
      />
      <StatCard
        icon={<Clock className="w-4 sm:w-5 h-4 sm:h-5 !text-status-warning" />}
        label="Pending"
        value="$0.00"
        iconBgColor="!bg-status-warning/20"
        valueColor="!text-status-warning"
      />
      <StatCard
        icon={<TrendingUp className="w-4 sm:w-5 h-4 sm:h-5 !text-status-success" />}
        label="Growth"
        value="0%"
        iconBgColor="!bg-status-success/20"
        valueColor="!text-status-success"
      />
    </div>

    {/* Affiliate Collection History */}
    <div className="!bg-white dark:!bg-dark-card rounded-xl">
      <div className="px-4 sm:px-6 py-3 sm:py-4 !border-b !border-gray-200 dark:!border-dark-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 !bg-status-success/20 rounded-lg">
            <TrendingUp className="w-4 h-4 !text-status-success" />
          </div>
          <h2 className="!text-base sm:!text-lg !font-semibold !text-gray-900 dark:!text-white">Affiliate Collection History</h2>
        </div>
      </div>
      <div className="p-4 sm:p-6">
        <SearchFilterBar
          showDownload
          onDownload={() => console.log('Download CSV')}
          statusOptions={[
            { value: '', label: 'All Status' },
            { value: 'active', label: 'Active' },
            { value: 'settled', label: 'Settled' },
          ]}
        />
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="min-w-[500px] px-4 sm:px-0">
            <table className="w-full">
              <thead>
                <tr className="!border-b !border-gray-200 dark:!border-dark-border">
                  <th className="px-3 sm:px-4 py-3 text-left !text-xs !font-medium !text-gray-500 dark:!text-dark-text-muted">Date</th>
                  <th className="px-3 sm:px-4 py-3 text-left !text-xs !font-medium !text-gray-500 dark:!text-dark-text-muted">Referred User</th>
                  <th className="px-3 sm:px-4 py-3 text-left !text-xs !font-medium !text-gray-500 dark:!text-dark-text-muted">Commission Earned</th>
                  <th className="px-3 sm:px-4 py-3 text-left !text-xs !font-medium !text-gray-500 dark:!text-dark-text-muted">Pending Commission</th>
                </tr>
              </thead>
              <tbody className="!divide-y !divide-gray-200 dark:!divide-dark-border">
                {affiliateHistory.map((item, index) => (
                  <tr key={index} className="hover:!bg-gray-50 dark:hover:!bg-dark-card-elevated/50">
                    <td className="px-3 sm:px-4 py-3 sm:py-4 !text-xs sm:!text-sm !text-gray-600 dark:!text-dark-text-secondary">{item.date}</td>
                    <td className="px-3 sm:px-4 py-3 sm:py-4 !text-xs sm:!text-sm !text-gray-900 dark:!text-white">{item?.user_info?.fullname || ''}</td>
                    <td className="px-3 sm:px-4 py-3 sm:py-4 !text-xs sm:!text-sm !text-status-success !font-medium">{item.earned}</td>
                    <td className="px-3 sm:px-4 py-3 sm:py-4 !text-xs sm:!text-sm !text-status-warning !font-medium">{item.pending}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <Panigation
          currentPage={historyPage}
          totalPages={Math.ceil(historyTotal / 10)}
          onPageChange={() => {
            setHistoryPage(historyPage);
          }} />
      </div>
    </div>
  </>;
}